import { useCallback, useMemo, useState } from 'react'
import { View, Text, ScrollView } from 'react-native'
import type { StyleProp, ViewStyle } from 'react-native'
import { useTheme } from '../../../theme/useTheme'
import { ACCOUNT_ADMIN_NAV, type AccountAdminNavId } from '../../constants/accountAdmin.constants'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import { getAccountAdminStartHerePanelStyles } from './AccountAdminStartHerePanel.styles'

export interface AccountAdminStartHerePanelProps {
  onNavigateToNav: (id: AccountAdminNavId) => void
  embedInSiteWebOuterScroll?: boolean
  /** Unused — kept so AccountAdminTabContent can pass these without a type error. */
  joinCode?: string
  worldCupSquadInviteCode?: string | null
  billingUnlocked?: boolean
}

export function AccountAdminStartHerePanel({
  onNavigateToNav,
  embedInSiteWebOuterScroll = false,
}: AccountAdminStartHerePanelProps) {
  const theme = useTheme()
  const copy = useAccountAdminCopy()
  const guide = copy.setupGuide
  const styles = useMemo(() => getAccountAdminStartHerePanelStyles(theme), [theme])
  const [pressedLink, setPressedLink] = useState<AccountAdminNavId | null>(null)

  const handleLinkPressIn = useCallback((id: AccountAdminNavId) => {
    setPressedLink(id)
  }, [])

  const handleLinkPressOut = useCallback(() => {
    setPressedLink(null)
  }, [])

  const renderGuideLine = useCallback(
    (
      rowStyle: StyleProp<ViewStyle>,
      before: string,
      linkLabel: string,
      navId: AccountAdminNavId,
      after: string,
    ) => (
      <View style={rowStyle}>
        <Text style={styles.stepText}>
          {before}
          <Text
            accessibilityRole="link"
            onPress={() => onNavigateToNav(navId)}
            onPressIn={() => handleLinkPressIn(navId)}
            onPressOut={handleLinkPressOut}
            style={[styles.stepLink, pressedLink === navId && styles.stepLinkPressed]}
          >
            {linkLabel}
          </Text>
          {after}
        </Text>
      </View>
    ),
    [handleLinkPressIn, handleLinkPressOut, onNavigateToNav, pressedLink, styles],
  )

  const startHereInner = (
    <View>
      <Text style={styles.title}>{copy.START_HERE}</Text>
      <Text style={styles.intro}>{copy.START_HERE_INTRO}</Text>
      <Text style={styles.guideTitle}>{guide.TITLE}</Text>
      {renderGuideLine(
        styles.stepRow,
        guide.LINE_BRANDING_BEFORE,
        guide.LINE_BRANDING_LINK,
        ACCOUNT_ADMIN_NAV.BRANDING,
        guide.LINE_BRANDING_AFTER,
      )}
      {renderGuideLine(
        styles.stepRow,
        guide.LINE_MEMBERS_BEFORE,
        copy.MEMBERS,
        ACCOUNT_ADMIN_NAV.MEMBERS,
        guide.LINE_MEMBERS_AFTER,
      )}
      {renderGuideLine(
        styles.stepRowLast,
        guide.LINE_SETTINGS_BEFORE,
        copy.SETTINGS,
        ACCOUNT_ADMIN_NAV.SETTINGS,
        guide.LINE_SETTINGS_AFTER,
      )}
    </View>
  )

  if (embedInSiteWebOuterScroll) {
    return (
      <View style={[styles.embeddedBody, styles.scrollPlain]}>{startHereInner}</View>
    )
  }

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scroll}
      showsVerticalScrollIndicator={false}
    >
      {startHereInner}
    </ScrollView>
  )
}
